import { useEffect, useMemo, useRef } from 'react';
import {
    RenderIf,
    RainbowThemeContainer,
    MarkdownOutput,
} from 'react-rainbow-components';
import LoadingShape from 'components/LoadingShape';
import { getFormatter } from 'data/services/date/formatter';
import ReportButton from 'components/ReportButton';
import { PURPLE_200 } from '../../../constants';
import {
    BubbleContainer,
    MessageContainer,
    StyledSaraAvatar,
    SummaryContainer,
    SummaryHeader,
    DateLabel,
    ReportButtonContainer,
    StyledMarkdownOutput,
} from './styled';

interface OpportunitySummaryProps {
    agentId: string;
    opportunityId: string;
    header?: string;
    summary?: {
        id: string;
        summary: string;
        updatedAt: string;
    };
    isLoading?: boolean;
    date?: Date;
    showDate?: boolean;
}

const theme = {
    rainbow: {
        palette: {
            mainBackground: PURPLE_200,
        },
    },
};

const OpportunitySummary = ({
    agentId,
    opportunityId,
    header,
    summary,
    isLoading,
    date,
    showDate = false,
}: OpportunitySummaryProps) => {
    const markdownRef = useRef<HTMLDivElement>(null);

    const formattedTime = useMemo(
        () => getFormatter('en-US', { hour12: true, hour: 'numeric', minute: '2-digit' }).format(date),
        [date],
    );

    const summaryId = summary?.id || (date ? date.toISOString() : opportunityId);

    useEffect(() => {
        if (markdownRef.current) {
            const links = markdownRef.current.querySelectorAll('a');
            links.forEach((link) => {
                link.setAttribute('target', '_blank');
                link.setAttribute('rel', 'noopener noreferrer');
            });
        }
    }, [summary?.summary]);

    return (
        <SummaryContainer>
            <StyledSaraAvatar />
            <BubbleContainer>
                <SummaryHeader>
                    <div>
                        {header}
                        <RenderIf isTrue={showDate && date}>
                            <DateLabel>{`at ${formattedTime}`}</DateLabel>
                        </RenderIf>
                    </div>
                    <RenderIf isTrue={summary && !isLoading}>
                        <ReportButtonContainer>
                            <ReportButton
                                label="Report summary"
                                agentId={agentId}
                                context={{
                                    type: 'opportunity-summary',
                                    opportunityId,
                                    summaryId,
                                    summary: summary?.summary,
                                }}
                                disabled={!summary}
                            />
                        </ReportButtonContainer>
                    </RenderIf>
                </SummaryHeader>
                <MessageContainer>
                    <RenderIf isTrue={isLoading}>
                        <RainbowThemeContainer theme={theme}>
                            <LoadingShape shape="rounded-rect" width="100%" height="10px" />
                            <LoadingShape shape="rounded-rect" width="60%" height="10px" />
                        </RainbowThemeContainer>
                    </RenderIf>
                    <RenderIf isTrue={!isLoading}>
                        <StyledMarkdownOutput ref={markdownRef}>
                            <MarkdownOutput value={summary?.summary || ''} />
                        </StyledMarkdownOutput>
                    </RenderIf>
                </MessageContainer>
            </BubbleContainer>
        </SummaryContainer>
    );
};

export default OpportunitySummary;
